import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface DeliveryConfirmation {
  id?: number;
  loadId: number | null;
  deliveryDate: Date;
  deliveryTime: string;
  driverId: number | null;
  customerSignature: string; // Base64 image
  customerName: string;
  customerTitle: string;
  receivedCondition: "good" | "damaged" | "partial";
  damageDetails: string;
  notes: string;
  photos: File[];
  gpsCoordinates: {
    latitude: number | null;
    longitude: number | null;
  };
  deliveryAddress: string;
  actualItems: {
    itemId: number;
    delivered: boolean;
    condition: "good" | "damaged" | "missing";
    notes: string;
  }[];
  mileageStart: number | null;
  mileageEnd: number | null;
  fuelLevel: number | null;
  equipmentCondition: string;
  returnedToYard: boolean;
  returnTime: string;
}

class DeliveryConfirmationWizardStore {
  rootStore: RootStore;

  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  confirmationData: DeliveryConfirmation = {
    loadId: null,
    deliveryDate: new Date(),
    deliveryTime: "",
    driverId: null,
    customerSignature: "",
    customerName: "",
    customerTitle: "",
    receivedCondition: "good",
    damageDetails: "",
    notes: "",
    photos: [],
    gpsCoordinates: {
      latitude: null,
      longitude: null,
    },
    deliveryAddress: "",
    actualItems: [],
    mileageStart: null,
    mileageEnd: null,
    fuelLevel: null,
    equipmentCondition: "",
    returnedToYard: false,
    returnTime: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateConfirmationData = <K extends keyof DeliveryConfirmation>(
    field: K,
    value: DeliveryConfirmation[K]
  ) => {
    this.confirmationData[field] = value;
  };

  selectLoad = (loadId: number) => {
    const loadStore = this.rootStore.loadStore as ServiceStore;
    const load = loadStore?.entities.find((l: any) => l.id === loadId);

    runInAction(() => {
      this.confirmationData.loadId = loadId;
      if (load) {
        this.confirmationData.deliveryAddress = load.deliveryAddress || "";
        // Initialize actual items from load
        this.initializeActualItems(load);
      }
    });
  };

  initializeActualItems = (load: any) => {
    // TODO: Get items from load when available
    // For now, create empty items array
    this.confirmationData.actualItems = [];
  };

  updateItemCondition = (itemId: number, condition: "good" | "damaged" | "missing", notes: string = "") => {
    this.confirmationData.actualItems = this.confirmationData.actualItems.map(item =>
      item.itemId === itemId
        ? { ...item, condition, notes, delivered: condition !== "missing" }
        : item
    );
  };

  addPhotos = (files: FileList) => {
    const newFiles = Array.from(files).filter(file => file.type.startsWith("image/"));
    this.confirmationData.photos = [...this.confirmationData.photos, ...newFiles];
  };

  removePhoto = (index: number) => {
    this.confirmationData.photos = this.confirmationData.photos.filter((_, i) => i !== index);
  };

  captureGPSLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          runInAction(() => {
            this.confirmationData.gpsCoordinates = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
          });
        },
        (error) => {
          this.setError("Failed to capture GPS location: " + error.message);
        }
      );
    } else {
      this.setError("GPS location not supported by this device");
    }
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Load Selection
        if (!this.confirmationData.loadId) {
          this.setError("Please select a load");
          return false;
        }
        if (!this.confirmationData.driverId) {
          this.setError("Please select the driver");
          return false;
        }
        break;

      case 1: // Delivery Details
        if (!this.confirmationData.deliveryTime) {
          this.setError("Please enter delivery time");
          return false;
        }
        if (!this.confirmationData.customerName) {
          this.setError("Please enter customer name");
          return false;
        }
        if (!this.confirmationData.customerSignature) {
          this.setError("Please capture customer signature");
          return false;
        }
        break;

      case 2: // Item Inspection
        if (this.confirmationData.receivedCondition === "damaged" && !this.confirmationData.damageDetails) {
          this.setError("Please provide damage details");
          return false;
        }
        break;

      case 3: // Vehicle Return
        if (this.confirmationData.mileageEnd !== null && this.confirmationData.mileageStart !== null) {
          if (this.confirmationData.mileageEnd < this.confirmationData.mileageStart) {
            this.setError("End mileage cannot be less than start mileage");
            return false;
          }
        }
        break;
    }

    return true;
  };

  submitConfirmation = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create delivery confirmation record when backend service is available
      // Update load status to delivered
      if (this.confirmationData.loadId) {
        const loadStore = this.rootStore.loadStore as ServiceStore;
        await loadStore?.patch(this.confirmationData.loadId, {
          status: "delivered",
          deliveredAt: new Date().toISOString(),
          deliveredBy: this.confirmationData.driverId,
        });
      }

      runInAction(() => {
        this.setSuccess("Delivery confirmation submitted successfully!");
        this.activeStep = 5; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to submit delivery confirmation");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.confirmationData = {
      loadId: null,
      deliveryDate: new Date(),
      deliveryTime: "",
      driverId: null,
      customerSignature: "",
      customerName: "",
      customerTitle: "",
      receivedCondition: "good",
      damageDetails: "",
      notes: "",
      photos: [],
      gpsCoordinates: {
        latitude: null,
        longitude: null,
      },
      deliveryAddress: "",
      actualItems: [],
      mileageStart: null,
      mileageEnd: null,
      fuelLevel: null,
      equipmentCondition: "",
      returnedToYard: false,
      returnTime: "",
    };
  };

  // Computed values
  get loads() {
    const loadStore = this.rootStore.loadStore as ServiceStore;
    return loadStore?.entities.filter((l: any) => l.status === "dispatched") || [];
  }

  get drivers() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities.filter((u: any) => u.role === "Driver") || [];
  }

  get selectedLoad() {
    if (!this.confirmationData.loadId) return null;
    return this.loads.find((l: any) => l.id === this.confirmationData.loadId);
  }

  get selectedDriver() {
    if (!this.confirmationData.driverId) return null;
    return this.drivers.find((d: any) => d.id === this.confirmationData.driverId);
  }

  get totalMileage() {
    if (this.confirmationData.mileageStart && this.confirmationData.mileageEnd) {
      return this.confirmationData.mileageEnd - this.confirmationData.mileageStart;
    }
    return 0;
  }

  get hasLocation() {
    return this.confirmationData.gpsCoordinates.latitude !== null &&
           this.confirmationData.gpsCoordinates.longitude !== null;
  }

  get deliveredItemsCount() {
    return this.confirmationData.actualItems.filter(item => item.delivered).length;
  }

  get damagedItemsCount() {
    return this.confirmationData.actualItems.filter(item => item.condition === "damaged").length;
  }
}

export default DeliveryConfirmationWizardStore;